import * as React from 'react';
import { useApiTrigger } from '../../hooks/useApiTrigger';
import {
    PivotGrid,
    PivotGridContainer,
    PivotGridConfigurator,
    usePivotLocalDataService,
    PivotGridConfiguratorButton,
    sumAggregate,
    minAggregate,
    maxAggregate,
    averageAggregate,
} from '@progress/kendo-react-pivotgrid';
import { Loader } from '@progress/kendo-react-indicators';
import { Typography } from '@mui/material';
import { Paper } from '@mui/material';
import { Box } from '@mui/material';
import { parsePivotConfig } from './Parsepivotconfig';
import '../../../../styles/components/kendo/_pivotGrid.scss';

/* ------------------ MAPPERS ------------------ */
const aggregateMap = {
    sum: sumAggregate,
    min: minAggregate,
    max: maxAggregate,
    average: averageAggregate,
    avg: averageAggregate,
};

const mapDimensions = (dimensions) => {
    const result = {};
    Object.entries(dimensions).forEach(([key, d]) => {
        result[key] = {
            caption: d.caption,
            displayValue: (item) => item[d.field],
            sortValue: (value) => value,
        };
    });
    return result;
};

const mapMeasures = (measures) =>
    measures.map((m) => {
        const aggregate = aggregateMap[m.aggregate];
        if (!aggregate) {
            throw new Error(`Aggregate no soportado: ${m.aggregate}`);
        }
        return { name: m.name, value: (item) => item[m.field], aggregate };
    });

const GridPivot = React.forwardRef(({ data, filters, returnData }, ref) => {
    const { dto } = useApiTrigger(data, filters);
    const safeData = React.useMemo(() => {
        if (!dto) return [];
        if (Array.isArray(dto)) return dto;
        if (typeof dto === 'object') return [dto];
        return [];
    }, [dto]);
    const [showConfigurator, setShowConfigurator] = React.useState(false);
    const serviceArgs = React.useMemo(() => {
        const parsed = parsePivotConfig(data.config.columns);
        const dimensions = mapDimensions(parsed.dimensions);
        return {
            data: dto,
            dimensions,
            measures: mapMeasures(parsed.measures),
            defaultRowAxes: parsed.defaultRowAxes || [],
            defaultColumnAxes: parsed.defaultColumnAxes || [],
            defaultMeasureAxes: parsed.defaultMeasureAxes || [],
            defaultSort: parsed.defaultSort || [],
            defaultFilter: parsed.defaultFilter || [],
        };
    }, [dto, data.config]);
    const getData = () => returnData(dto, data.accion || 'Reporte_Grafica');
    React.useImperativeHandle(ref, () => ({ exportChartData: getData }));

    const { pivotProps, configuratorProps, state } = usePivotLocalDataService(serviceArgs);
    const handleButtonClick = React.useCallback(() => {
        setShowConfigurator(!showConfigurator);
    }, [showConfigurator]);

    return (
        <>
            {(safeData.length === 0 && !state.loading) ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height={400}
                    flexDirection="column"
                >
                    <Typography variant="h6" color="text.secondary">
                        No hay datos disponibles
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Para este filtro o período no se encontraron registros.
                    </Typography>
                </Box>
            ) : (
                <Paper>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        py={1}
                        borderBottom="1px solid"
                        borderColor="divider"
                    >
                        <Typography variant="h6" fontWeight={600}>
                            {data.accion || 'Reporte_Grafica'}
                        </Typography>
                    </Box>
                    <Box position="relative">
                        <PivotGridContainer>
                            <PivotGrid {...pivotProps} style={{ height: state.loading ? 350 : undefined, maxHeight: state.loading ? undefined : 700 }} />
                            {showConfigurator && <PivotGridConfigurator {...configuratorProps} />}
                            <PivotGridConfiguratorButton onClick={handleButtonClick} />
                            {state.loading && <Loader style={{
                                position: 'absolute',
                                top: '60%',
                                left: '50%',
                                transform: 'translate(-50%,-50%)'
                            }}
                                size="large" />
                            }
                        </PivotGridContainer>
                    </Box>
                </Paper>
            )}
        </>
    );
});
export default GridPivot;